import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import { useDeliveryAddress } from '../context/DeliveryAddressContext';
import './AddressSelector.css';

const AddressSelector = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { showError } = useNotification();
  const { currentAddress, setCurrentAddress } = useDeliveryAddress();
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchAddress, setSearchAddress] = useState('');
  const autocompleteInputRef = useRef(null);

  useEffect(() => {
    // Initialize Google Places Autocomplete for address search
    if (window.google && window.google.maps && window.google.maps.places && autocompleteInputRef.current) {
      const autocomplete = new window.google.maps.places.Autocomplete(
        autocompleteInputRef.current,
        { types: ['address'] }
      );

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (place.geometry && place.geometry.location) {
          const addressData = {
            label: place.formatted_address,
            street: place.formatted_address,
            city: extractCity(place.address_components),
            zipCode: extractZipCode(place.address_components),
            addressDetails: '',
            coordinates: {
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng()
            }
          };
          setCurrentAddress(addressData);
          setSearchAddress(place.formatted_address);
          setShowDropdown(false);
        }
      });
    }
  }, [showDropdown, setCurrentAddress]);

  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (showDropdown && !event.target.closest('.address-selector')) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown]);

  const extractCity = (components) => {
    for (const component of components) {
      if (component.types.includes('locality')) {
        return component.long_name;
      }
    }
    return '';
  };

  const extractZipCode = (components) => {
    for (const component of components) {
      if (component.types.includes('postal_code')) {
        return component.long_name;
      }
    }
    return '';
  };

  const handleUseCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;

          // Always get city name from coordinates - never show coordinates
          if (window.google && window.google.maps) {
            const geocoder = new window.google.maps.Geocoder();
            geocoder.geocode({ location: { lat, lng } }, (results, status) => {
              if (status === 'OK' && results[0]) {
                const cityName = extractCity(results[0].address_components || []);
                
                // If no city found, try to get locality or administrative_area_level_1
                let locationName = cityName;
                if (!locationName && results[0].address_components) {
                  for (const component of results[0].address_components) {
                    if (component.types.includes('administrative_area_level_1')) {
                      locationName = component.long_name;
                      break;
                    } else if (component.types.includes('administrative_area_level_2')) {
                      locationName = component.long_name;
                      break;
                    }
                  }
                }
                
                // Fallback to first part of formatted address if still no city
                if (!locationName && results[0].formatted_address) {
                  locationName = results[0].formatted_address.split(',')[0];
                }
                
                const addressData = {
                  label: locationName || 'Current Location',
                  street: results[0].formatted_address || '',
                  city: cityName || locationName || '',
                  zipCode: extractZipCode(results[0].address_components || []),
                  addressDetails: '',
                  coordinates: { lat, lng }
                };
                setCurrentAddress(addressData);
                setShowDropdown(false);
              } else {
                // If geocoding fails, show generic message instead of coordinates
                showError('Unable to determine city name from your location. Please use address search instead.');
              }
            });
          } else {
            // If Google Maps is not loaded, prompt user to use address search
            showError('Location services require Google Maps. Please use address search instead.');
          }
        },
        (error) => {
          console.error('Error getting location:', error);
          showError('Unable to get your location');
        }
      );
    }
  };

  const handleSelectSavedAddress = (address) => {
    // Ensure the address has the correct structure with coordinates
    // Check if coordinates exist and are valid
    if (address.coordinates && 
        address.coordinates.lat !== undefined && 
        address.coordinates.lat !== null &&
        address.coordinates.lng !== undefined && 
        address.coordinates.lng !== null) {
      // Create a completely new object to ensure React detects the change
      const lat = parseFloat(address.coordinates.lat);
      const lng = parseFloat(address.coordinates.lng);
      
      // Validate parsed coordinates
      if (isNaN(lat) || isNaN(lng)) {
        console.warn('Selected saved address has invalid coordinate values:', address);
        showError('This saved address has invalid location coordinates. Please use address search or current location instead.');
        return;
      }
      
      const addressData = {
        label: address.label || '',
        street: address.street || '',
        city: address.city || '',
        zipCode: address.zipCode || '',
        addressDetails: address.addressDetails || '',
        deliveryInstructions: address.deliveryInstructions || '',
        coordinates: {
          lat: lat,
          lng: lng
        }
      };
      
      // Force React to detect the change by creating a completely new object reference
      setCurrentAddress({ ...addressData });
      setShowDropdown(false);
    } else {
      console.warn('Selected saved address does not have valid coordinates:', address);
      showError('This saved address does not have location coordinates. Please use address search or current location instead.');
    }
  };

  const getDisplayAddress = () => {
    if (!currentAddress) return t('nav.selectAddress') || 'Select delivery address';
    
    // Always prefer street address if available; append details (e.g. staircase, apartment) if set
    if (currentAddress.street) {
      return currentAddress.addressDetails
        ? `${currentAddress.street} — ${currentAddress.addressDetails}`
        : currentAddress.street;
    }
    
    // If no street but we have coordinates, try to get address
    if (currentAddress.coordinates && currentAddress.coordinates.lat && currentAddress.coordinates.lng && window.google && window.google.maps) {
      // If label contains coordinates (like "60.1699, 24.9384"), don't show it
      const label = currentAddress.label || '';
      if (!label.match(/^\d+\.\d+,\s*\d+\.\d+$/)) {
        return label;
      }
      // If it's coordinates, return default message to encourage user to select address
      return t('nav.selectAddress') || 'Select delivery address';
    }
    
    // Use label if it's not coordinates
    if (currentAddress.label) {
      const label = currentAddress.label;
      // Check if label looks like coordinates
      if (!label.match(/^\d+\.\d+,\s*\d+\.\d+$/)) {
        return label;
      }
    }
    
    return t('nav.selectAddress') || 'Select delivery address';
  };

  return (
    <div className="address-selector">
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="address-display-btn"
      >
        <span className="address-icon"><i className="fas fa-map-marker-alt"></i></span>
        <span className="address-text">{getDisplayAddress()}</span>
        <span className="address-arrow">▼</span>
      </button>

      {showDropdown && (
        <div className="address-dropdown">
          <div className="address-search-section">
            <input
              ref={autocompleteInputRef}
              type="text"
              placeholder={t('nav.searchAddress') || 'Search address...'}
              value={searchAddress}
              onChange={(e) => setSearchAddress(e.target.value)}
              className="address-search-input"
            />
            <button onClick={handleUseCurrentLocation} className="btn btn-primary btn-sm">
              {t('nav.useCurrentLocation') || 'Use Current Location'}
            </button>
          </div>

          {currentAddress && (
            <div className="address-details-section">
              <label htmlFor="address-details-input">{t('nav.addressDetails') || 'Staircase, apartment number (optional)'}</label>
              <input
                id="address-details-input"
                type="text"
                placeholder={t('nav.addressDetailsPlaceholder') || 'e.g. B, 12'}
                value={currentAddress.addressDetails || ''}
                onChange={(e) => setCurrentAddress({ ...currentAddress, addressDetails: e.target.value })}
                className="address-details-input"
              />
            </div>
          )}

          {user?.savedAddresses && user.savedAddresses.length > 0 && (
            <div className="saved-addresses-section">
              <h4>{t('nav.savedAddresses') || 'Saved Addresses'}</h4>
              {user.savedAddresses.map((address) => (
                <button
                  key={address._id}
                  onClick={() => handleSelectSavedAddress(address)}
                  className="saved-address-item"
                >
                  <div>
                    <strong>{address.label}</strong>
                    <p>{address.street}, {address.city} {address.zipCode}{address.addressDetails ? ` — ${address.addressDetails}` : ''}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AddressSelector;
